// Figma: no frame provided yet — built using the established design system (stat-card
// pattern from StatCards.js) per the "extrapolate" decision.
// LOGIC PORTED from LC2's CampaignInfo.js (commons/components/HomeTabs/Today/Campaign/):
// same stat fields (Type, Status, Date created, Unit type, Residents, Lease term minimum,
// Stake Checking residents) computed from the campaign + `entities.residents`. The
// floor-plan-count tooltip (multiple unit types) is simplified to a plain "N floor plans"
// label instead of a hover tooltip.
import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {Box, Stack, Typography} from '@mui/material';

import {getResidents} from '../../app/store/reducers/entities';
import {formatNumber} from '../../commons/constants/stylingConstants';
import {formatDateString} from '../../commons/constants/dateConstants';
import {hasApplied} from '../../commons/constants/residentsInfoConstants';
import {tokens} from '../../themes/ThemeCustomization';

const StatItem = ({label, value}) => (
    <Stack sx={{flex: 1, minWidth: '140px', px: 2, py: 1.5, gap: 0.25}}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="subtitle1" fontWeight={600}>
            {value}
        </Typography>
    </Stack>
);

const CampaignInfo = ({campaign}) => {
    const residents = useSelector(getResidents);

    const info = useMemo(() => {
        if (!campaign) return null;

        const isDraft = campaign.status?.toLowerCase() === 'draft';
        const campaignType = campaign.name
            ? campaign.name.includes('StakePro')
                ? 'StakePro'
                : campaign.name.includes('StakeFree')
                  ? 'StakeFree'
                  : campaign.subtype
            : campaign.subtype;

        const dateObj = new Date(formatDateString(campaign.start_date));
        const dateCreated = isNaN(dateObj.getTime())
            ? null
            : dateObj.toLocaleDateString('en-US', {year: 'numeric', month: '2-digit', day: '2-digit'});

        const unitTypesRaw = campaign.target?.unit_types?.split(';') || [];
        const unitTypeLabel = unitTypesRaw.length > 1 ? `${unitTypesRaw.length} floor plans` : unitTypesRaw[0] || '-';

        const isStakePlus = campaign.subtype && campaign.subtype !== 'Stake1';
        const residentCount = residents.filter((r) => r.renter_campaign__c === campaign.id).length;
        const residentsLabel = isStakePlus ? 'Stake+ residents' : 'Total residents';

        const stakeCheckingResidents = residents.filter(
            (r) => hasApplied.includes(r.stake_debit_status__c) && campaign.sfid === r.renter_campaign__c
        ).length;

        return {
            isDraft,
            campaignType,
            dateCreated,
            unitTypeLabel,
            residentsLabel,
            residentCount: formatNumber(residentCount),
            leaseTermMin: campaign.target?.lease_term_months_min || 0,
            stakeCheckingResidents
        };
    }, [campaign, residents]);

    if (!info) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                border: `1px solid ${tokens.color.neutral200}`,
                borderRadius: '6px',
                backgroundColor: tokens.color.white,
                '& > *:not(:last-child)': {borderRight: `1px solid ${tokens.color.neutral200}`}
            }}
        >
            <StatItem label="Type" value={info.campaignType} />
            <StatItem label="Status" value={campaign.status} />
            {!info.isDraft && <StatItem label="Date created" value={info.dateCreated} />}
            <StatItem label="Unit type" value={info.unitTypeLabel} />
            <StatItem label={info.residentsLabel} value={info.residentCount} />
            <StatItem label="Lease term minimum" value={info.leaseTermMin} />
            <StatItem label="Stake Checking residents" value={info.stakeCheckingResidents} />
        </Box>
    );
};

export default CampaignInfo;
